import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import {
  collectVaultSessionRoleTokens,
  vaultCurrentTenantIdFromSession,
  vaultCurrentUserIdFromSession,
  vaultResolveAdminUsersActor,
  vaultResolveIsSuperadmin,
  vaultUserCanTenantAdminFromLocalStorage,
  vaultUserIsSuperadminFromSession,
  type VaultAdminUsersActor,
} from './vault-user-roles.util';
import {
  vaultResolveActiveTenantContext,
  type VaultActiveTenantContext,
} from './vault-active-tenant.util';

/**
 * Sesión Vault canónica: roles y tenant del usuario logueado desde JWT / user_data (Simappe).
 * No sustituye APIs DataVault para administrar otros usuarios.
 */
@Injectable({ providedIn: 'root' })
export class VaultSessionService {
  private readonly auth = inject(AuthService);

  /** Authorities normalizados del JWT + user_data. */
  getRoleAuthorities(): string[] {
    return this.auth.getUserPermissions().map((a) => String(a ?? '').trim()).filter(Boolean);
  }

  /** IDs numéricos de rol Simappe (menú `read-by-roles-grouped`). */
  getRoleIds(): number[] {
    const roles = this.auth.getUserRoles();
    return roles
      .map((r: { id?: unknown }) => (typeof r?.id === 'number' ? r.id : Number(r?.id)))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  getUserId(): string {
    return vaultCurrentUserIdFromSession();
  }

  getTenantId(): string {
    return vaultCurrentTenantIdFromSession();
  }

  /** Tenant activo + rol + nombre de empresa (paridad legacy `currentTenant` + `getCurrentUserRole`). */
  getActiveTenantContext(dashboardRole?: string | null): VaultActiveTenantContext {
    return vaultResolveActiveTenantContext({ dashboardRole });
  }

  isSuperadmin(): boolean {
    return vaultUserIsSuperadminFromSession();
  }

  canTenantAdmin(): boolean {
    return vaultUserCanTenantAdminFromLocalStorage();
  }

  hasRole(roleName: string): boolean {
    return this.auth.hasRole(roleName);
  }

  getAdminUsersActor(dashboardRole?: string | null): VaultAdminUsersActor {
    return vaultResolveAdminUsersActor(dashboardRole);
  }

  canManageAdminUsers(dashboardRole?: string | null): boolean {
    return this.getAdminUsersActor(dashboardRole) !== 'denied';
  }

  resolveIsSuperadmin(dashboardRole?: string | null): boolean {
    return vaultResolveIsSuperadmin(dashboardRole);
  }

  /** Tokens de rol en sesión (debug / matrices). */
  getSessionRoleTokens(): string[] {
    return collectVaultSessionRoleTokens();
  }
}
